import { useCallback } from 'react';

import type { PermissionValue } from '@/constants/Permission';

import { useGetUserPermissions } from '@/api/directory';

/**
 * Permission checks for the current user.
 *
 * `can` takes one or more permissions and returns true only when the user holds
 * every one of them, so a call reads as the whole requirement for an action:
 *
 * ```ts
 * const { can } = useUserPermissions();
 *
 * can(Permission.ViewPerformanceProfile);
 * can(Permission.ViewPerformanceProfile, Permission.ManageCompetencies);
 * ```
 *
 * While the query is still loading, and if it fails, `can` returns false. Gating
 * UI on it therefore hides an element until the permission is known, rather than
 * flashing it and taking it away, or leaving it visible when the answer never
 * arrives.
 */
export const useUserPermissions = () => {
    const { data: permissions, isLoading, isError, refetch } = useGetUserPermissions();

    const can = useCallback(
        (...required: PermissionValue[]) =>
            permissions !== undefined && required.every((permission) => permissions[permission]),
        [permissions],
    );

    return { can, permissions, isLoading, isError, refetch };
};
